import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Rocket, Bell, Eye, Play, Film } from "lucide-react";
import { toast } from "sonner";
import { api, apiErr } from "../lib/api";
import { WeeklyCountdown, useLiveProjectCount } from "../components/Countdown";
import { ShareBar } from "../components/ShareBar";
import { AcaPromiseBanner } from "../components/AcaPromiseBanner";
import Seo from "../components/Seo";
import { useSettings } from "../context/SettingsContext";
import { SOCIAL_NETWORKS } from "../lib/socials";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const imgUrl = (id, rev) => `${BACKEND}/api/publications/${id}/image${rev ? `?v=${rev}` : ""}`;

// Compteur des projets déjà déposés : logique hebdomadaire partagée (components/Countdown.jsx).
function ProjectsCounter() {
  const live = useLiveProjectCount();
  if (!live) return null;
  return (
    <div className="mt-6 flex items-center justify-center gap-3 rounded-xl border border-cyan/20 bg-cyan/5 px-5 py-4" data-testid="blog-projects-counter">
      <span className="font-display text-3xl font-900 tabular-nums text-cyan text-glow" data-testid="blog-projects-counter-value">{live.count}</span>
      <span className="text-left text-xs leading-snug text-ink-secondary">
        projets déjà déposés<br />
        <span className="text-[10px] uppercase tracking-[0.15em] text-ink-tertiary">Cette semaine</span>
      </span>
    </div>
  );
}

const BLOG_JSONLD = {
  "@context": "https://schema.org",
  "@type": "Blog",
  name: "APP CHALLENGE ACADEMY — Le Blog",
  description: "Le fonctionnement, le rôle et les coulisses de App Challenge Academy.",
  url: "https://appchallengeacademy.com/blog",
  inLanguage: "fr-FR",
  publisher: {
    "@type": "Organization",
    name: "APP CHALLENGE ACADEMY",
    url: "https://appchallengeacademy.com",
    logo: "https://appchallengeacademy.com/app-icon.png",
  },
};

export default function Blog() {
  const { settings } = useSettings();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [email, setEmail] = useState("");
  const [subscribing, setSubscribing] = useState(false);
  const origin = typeof window !== "undefined" ? window.location.origin : "";

  // Chargement des publications : état de chargement explicite + bouton « Réessayer »
  // en cas d'erreur — pour ne JAMAIS laisser croire qu'il n'y a aucun article
  // (au retour arrière depuis un article, le composant est remonté et rechargé).
  const loadPosts = () => {
    setLoading(true); setLoadError(false);
    api.get("/public/publications")
      .then((r) => setPosts(r.data))
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  };
  useEffect(() => { loadPosts(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubscribing(true);
    try {
      await api.post("/public/blog/subscribe", { email });
      toast.success("Merci ! Vous êtes abonné(e) à l'aventure ACA.");
      setEmail("");
    } catch (err) { toast.error(apiErr(err)); }
    finally { setSubscribing(false); }
  };

  const socials = SOCIAL_NETWORKS.filter((s) => (settings[s.key] || "").trim());

  return (
    <div data-testid="blog-page">
      <Seo title="Le Blog" description="Chaque semaine, plusieurs applications pourront être sélectionnées et créées gratuitement, avec et pour vous. Suivez le compte à rebours hebdomadaire et déposez votre idée." path="/blog" jsonLd={BLOG_JSONLD} />

      {/* Hero + compte à rebours */}
      <section className="relative overflow-hidden px-5 pt-36 pb-16 sm:px-8">
        <div className="pointer-events-none absolute -top-20 right-0 h-[500px] w-[500px] rounded-full bg-cyan/15 blur-[130px]" />
        <div className="mx-auto max-w-[1100px] text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-cyan/30 bg-cyan/10 px-4 py-1.5 text-[11px] uppercase tracking-[0.25em] text-cyan">
            <Rocket size={13} /> Le Blog · L’aventure ACA
          </div>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            className="mt-6 font-display text-4xl font-900 leading-[0.95] tracking-tighter text-white sm:text-6xl">
            APP CHALLENGE ACADEMY
          </motion.h1>
          <p className="mx-auto mt-5 max-w-2xl text-base text-ink-secondary">
            On vous dit tout : le fonctionnement, le rôle et les coulisses de l’Academy.
            Suivez l’évolution de l’aventure en direct.
          </p>

          <div className="mx-auto mt-10 max-w-2xl rounded-2xl border border-white/10 bg-surface/60 p-6 sm:p-8">
            <div className="text-[11px] uppercase tracking-[0.25em] text-cyan">Chaque semaine, plusieurs applications pourront être sélectionnées</div>
            <div className="mt-1 font-display text-lg font-700 text-white">et créées gratuitement, avec et pour vous</div>
            <div className="mt-2 space-y-1 text-center text-sm text-ink-secondary">
              <p>Une idée de business ou d'application ?</p>
              <p>
                <Link to="/app/deposer" className="font-600 text-cyan underline-offset-2 hover:underline" data-testid="blog-deposit-link">Déposez-les dès maintenant</Link>
              </p>
              <p>Sélection chaque lundi par notre comité.</p>
            </div>
            <div className="mt-6" data-testid="blog-weekly-countdown"><WeeklyCountdown /></div>
            <ProjectsCounter />

            <form onSubmit={subscribe} className="mt-8 flex flex-col gap-3 sm:flex-row" data-testid="blog-subscribe-form">
              <div className="relative flex-1">
                <Bell size={16} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-ink-tertiary" />
                <input
                  type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="Votre e-mail pour suivre l'aventure" data-testid="blog-subscribe-email"
                  className="w-full rounded-xl border border-white/15 bg-white/[0.06] py-3.5 pl-11 pr-4 text-sm text-white placeholder:text-ink-tertiary outline-none transition-colors focus:border-cyan focus:ring-1 focus:ring-cyan"
                />
              </div>
              <button type="submit" disabled={subscribing} data-testid="blog-subscribe-submit"
                className="group inline-flex items-center justify-center gap-2 rounded-xl bg-cyan px-6 py-3.5 text-sm font-600 uppercase tracking-wide text-void transition-all hover:glow-cyan disabled:opacity-60">
                {subscribing ? "…" : "S'abonner"}
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </button>
            </form>

            {socials.length > 0 && (
              <div className="mt-6 flex items-center justify-center gap-3">
                <span className="text-xs uppercase tracking-[0.2em] text-ink-tertiary">Suivez-nous</span>
                {socials.map((s) => (
                  <a key={s.key} href={settings[s.key]} target="_blank" rel="noopener noreferrer" data-testid={`blog-social-${s.key}`}
                    className="flex h-9 w-9 items-center justify-center rounded-full border border-white/15 text-ink-secondary transition-all hover:-translate-y-0.5 hover:border-cyan hover:text-cyan">
                    <s.icon size={16} />
                  </a>
                ))}
              </div>
            )}

            <div className="mt-6 flex flex-col items-center gap-3 border-t border-white/10 pt-6">
              <span className="text-xs uppercase tracking-[0.2em] text-ink-tertiary">Partager le compte à rebours</span>
              <ShareBar url={`${BACKEND}/api/share/blog`} title="APP CHALLENGE ACADEMY — Chaque semaine, plusieurs applications pourront être sélectionnées et créées gratuitement, avec et pour vous. Déposez votre idée !" compact />
            </div>
          </div>
        </div>
      </section>

      {/* Articles */}
      <section className="px-5 pb-24 sm:px-8">
        <div className="mx-auto max-w-[1200px]">
          <AcaPromiseBanner className="mb-12" />
          {loading ? (
            <div className="rounded-2xl border border-white/10 bg-surface p-16 text-center" data-testid="blog-loading">
              <p className="text-ink-secondary">Chargement des publications…</p>
            </div>
          ) : loadError ? (
            <div className="rounded-2xl border border-dashed border-white/15 p-16 text-center" data-testid="blog-load-error">
              <p className="text-ink-secondary">Impossible de charger les publications pour le moment.</p>
              <button onClick={loadPosts} data-testid="blog-retry"
                className="mt-4 inline-flex items-center gap-2 rounded-full bg-cyan px-6 py-2.5 text-sm font-600 uppercase tracking-wide text-void transition-all hover:glow-cyan">
                Réessayer
              </button>
            </div>
          ) : posts.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-white/15 p-16 text-center">
              <p className="text-ink-secondary">Les premières publications arrivent très bientôt. Abonnez-vous pour ne rien manquer.</p>
            </div>
          ) : (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {posts.map((p, i) => (
                <motion.article
                  key={p.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: (i % 3) * 0.06 }}
                  className="group flex flex-col overflow-hidden rounded-2xl border border-white/10 bg-surface transition-all hover:-translate-y-1 hover:border-cyan/40"
                  data-testid={`blog-card-${p.id}`}>
                  <Link to={`/blog/${p.id}`} className="relative block aspect-[16/10] overflow-hidden bg-elevated">
                    {(p.kind === "video" || p.has_video) ? (
                      <>
                        {/* La liste affiche UNIQUEMENT l'image de couverture travaillée pour
                            chaque vidéo — la lecture démarre dans l'article, jamais ici. */}
                        {p.has_image
                          ? <img src={imgUrl(p.id, p.image_rev)} alt={p.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" data-testid={`blog-card-cover-${p.id}`} />
                          : <div className="flex h-full items-center justify-center text-cyan/40"><Film size={40} /></div>}
                        <span className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-full bg-black/60 px-2.5 py-1 text-[10px] uppercase tracking-wider text-cyan"><Film size={11} /> Vidéo</span>
                        <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
                          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-cyan/90 text-void"><Play size={20} /></span>
                        </span>
                      </>
                    ) : p.has_image
                      ? <img src={imgUrl(p.id, p.image_rev)} alt={p.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                      : <div className="flex h-full items-center justify-center text-cyan/30"><Rocket size={40} /></div>}
                  </Link>
                  <div className="flex flex-1 flex-col p-5">
                    {p.kind !== "video" && p.episode_number && (
                      <div className="mb-2 text-[11px] uppercase tracking-[0.2em] text-cyan" data-testid={`blog-episode-${p.id}`}>Épisode {p.episode_number}</div>
                    )}
                    <Link to={`/blog/${p.id}`}>
                      <h2 className="font-display text-lg font-700 leading-snug text-white transition-colors group-hover:text-cyan line-clamp-2">{p.title}</h2>
                    </Link>
                    <p className="mt-2 flex-1 text-sm text-ink-secondary line-clamp-3">{p.excerpt}</p>
                    {p.hashtags && p.hashtags.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-1.5" data-testid={`blog-hashtags-${p.id}`}>
                        {p.hashtags.slice(0, 3).map((h, k) => (
                          <span key={k} className="rounded-full border border-white/10 bg-white/[0.04] px-2 py-0.5 text-[11px] text-cyan">{h}</span>
                        ))}
                      </div>
                    )}
                    <div className="mt-3 inline-flex items-center gap-1.5 text-xs text-ink-tertiary" data-testid={`blog-views-${p.id}`}>
                      <Eye size={13} className="text-cyan" /> {p.views || 0} vue{(p.views || 0) > 1 ? "s" : ""}
                    </div>
                    <div className="mt-4 flex items-center justify-between border-t border-white/5 pt-4">
                      <Link to={`/blog/${p.id}`} data-testid={`blog-read-${p.id}`} className="inline-flex items-center gap-1.5 text-sm text-cyan hover:underline">
                        Lire <ArrowRight size={14} />
                      </Link>
                      <ShareBar url={`${origin}/blog/${p.id}`} title={p.title} publicationId={p.id} compact />
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
